// composite.guard.ts
import { Injectable, Injector, Type } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
    UrlTree
} from '@angular/router';
import { Observable, of, from, concatMap, isObservable, catchError, tap } from 'rxjs';

type ClassGuardType = Type<any>;

function normalizeToObservable(
    result: Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree
): Observable<boolean | UrlTree> {
    if (isObservable(result)) {
        return result;
    }
    if (typeof result === 'boolean' || result instanceof UrlTree) {
        return of(result);
    }
    return from(result);
}

@Injectable({
    providedIn: 'root',
})
export class CompositeGuard {
    constructor(private injector: Injector) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean | UrlTree> {
        const guardsToRun = route.data['guards'] as ClassGuardType[] | undefined;

        if (!guardsToRun || guardsToRun.length === 0) {
            // console.log('CompositeGuard: No guards. Allowing access.');
            return of(true);
        }

        // console.log('CompositeGuard: Executing guards in order:', guardsToRun.map(g => g.name));

        // Start with an observable that emits true, to kick off the chain
        let currentGuardResult$: Observable<boolean | UrlTree> = of(true);

        for (const guardType of guardsToRun) {
            currentGuardResult$ = currentGuardResult$.pipe(
                concatMap((previousResult: boolean | UrlTree) => {
                    // If the previous guard in the chain returned false or a UrlTree,
                    // stop processing and propagate that result.
                    if (previousResult !== true) {
                        // console.log(`CompositeGuard: Previous guard failed/redirected (${guardType.name} will not run). Result:`, previousResult);
                        return of(previousResult); // Propagate the failure/redirect
                    }

                    // Previous guard passed, so execute the current guard
                    // console.log(`CompositeGuard: Executing ${guardType.name}.`);
                    let guardInstance: any;
                    try {
                        guardInstance = this.injector.get(guardType);
                    } catch (e) {
                        // console.error(`CompositeGuard: Failed to instantiate ${guardType.name}.`, e);
                        return of(false);
                    }

                    if (!guardInstance || typeof guardInstance.canActivate !== 'function') {
                        // console.error(`CompositeGuard: ${guardType.name} is invalid.`);
                        return of(false);
                    }

                    const executionResult = guardInstance.canActivate(route, state);
                    return normalizeToObservable(executionResult).pipe(
                        // tap(res => console.log(`CompositeGuard: ${guardType.name} raw result:`, res))
                    ); // Just return the normalized result
                }),
                // The catchError here is important if a guard's observable itself errors out
                // for reasons other than returning false/UrlTree (e.g., HTTP error in a service it calls)
                catchError(err => {
                    console.error(`CompositeGuard: Error during execution of a guard (${guardType.name}).`, err);
                    // Decide how to handle unexpected errors from a guard's observable.
                    // This is different from a guard *returning* false or UrlTree.
                    return of(false);
                })
            );
        }

        // The final result of the chain
        return currentGuardResult$.pipe(
            // tap(finalResult => console.log('CompositeGuard: Final result of chain:', finalResult))
        );
    }
}